import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { usePlayer } from "@/context/PlayerContext";
import { createFamily, generatePortrait } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Compass, Sparkles, Cog, Wand2, Loader2, Mail, ChevronLeft } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Profile() {
  const { t, i18n } = useTranslation();
  const { setPlayer, setFamilyId } = usePlayer();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [avatar, setAvatar] = useState("luna");
  const [mode, setMode] = useState("discovery");
  const [loading, setLoading] = useState(false);
  const [portraitDesc, setPortraitDesc] = useState("");
  const [portraitImg, setPortraitImg] = useState(null);
  const [portraitLoading, setPortraitLoading] = useState(false);

  const AVATARS = [
    { key: "luna",    label: t("profile.avatar_luna"),    emoji: "🌙" },
    { key: "sphinx",  label: t("profile.avatar_sphinx"),  emoji: "🐈" },
    { key: "knight",  label: t("profile.avatar_knight"),  emoji: "🛡️" },
    { key: "android", label: t("profile.avatar_android"), emoji: "🤖" },
  ];

  const handleGeneratePortrait = async (playerId) => {
    if (!portraitDesc.trim()) return null;
    setPortraitLoading(true);
    try {
      const res = await generatePortrait(playerId, portraitDesc, i18n.language?.slice(0, 2));
      setPortraitImg(res.image_base64);
      return res.image_base64;
    } catch (e) {
      toast.error(t("profile.portrait_error"));
      return null;
    } finally {
      setPortraitLoading(false);
    }
  };

  const handleStart = async () => {
    if (!name.trim()) {
      toast.error(t("profile.name_missing"));
      return;
    }
    setLoading(true);
    try {
      const family = await createFamily({
        parent_email: parentEmail.trim() || null,
        first_child_name: name,
        first_child_avatar: avatar,
        first_child_mode: mode,
      });
      const { getPlayer } = await import("@/lib/api");
      const childId = family.children_ids[0];
      const p = await getPlayer(childId);
      setFamilyId(family.id);

      let enriched = p;
      if (portraitDesc.trim()) {
        toast(t("profile.portrait_generating"));
        const img = await handleGeneratePortrait(p.id);
        if (img) enriched = { ...p, custom_portrait: img, portrait_description: portraitDesc };
      }
      setPlayer(enriched);
      if (enriched.custom_portrait) {
        toast.success(t("profile.portrait_ready", { name: p.name }));
        setTimeout(() => navigate("/levels"), 3200);
      } else {
        toast.success(t("profile.welcome", { name: p.name }));
        navigate("/levels");
      }
    } catch (e) {
      toast.error(t("profile.create_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen hero-radial grain flex items-center justify-center px-4 py-10">
      <div className="absolute top-5 right-5 z-30 flex items-center gap-3">
        <Link to="/" className="text-stone-300 hover:text-amber-200 flex items-center gap-1 text-sm" data-testid="profile-back-home">
          <ChevronLeft className="w-4 h-4" /> {t("common.back_home")}
        </Link>
        <LanguageSwitcher />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-2xl"
      >
        <div className="glass-strong rounded-3xl p-8 sm:p-10">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-300/90 font-bold">{t("profile.step")}</div>
          <h2 className="mt-2 font-serif text-4xl sm:text-5xl font-black text-stone-50">
            {t("profile.who_1")}<span className="italic text-amber-300">{t("profile.who_2")}</span>{t("profile.who_3")}
          </h2>

          <div className="mt-8 space-y-6">
            <div>
              <label className="block text-xs uppercase tracking-widest font-bold text-stone-300 mb-2">{t("profile.name_label")}</label>
              <Input
                data-testid="input-name"
                value={name}
                maxLength={24}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("profile.name_placeholder")}
                className="bg-black/40 border-white/15 h-12 text-lg font-serif focus-visible:ring-amber-400"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest font-bold text-stone-300 mb-2 flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" /> {t("profile.email_label")}
              </label>
              <Input
                data-testid="input-parent-email"
                type="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                placeholder={t("profile.email_placeholder")}
                className="bg-black/40 border-white/15 focus-visible:ring-teal-400"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest font-bold text-stone-300 mb-2">{t("profile.avatar_label")}</label>
              <div className="grid grid-cols-4 gap-3">
                {AVATARS.map((a) => (
                  <button
                    key={a.key}
                    onClick={() => setAvatar(a.key)}
                    data-testid={`avatar-${a.key}`}
                    className={`rounded-2xl border-2 p-3 flex flex-col items-center gap-1 transition-all ${
                      avatar === a.key ? "border-amber-400 bg-amber-500/10" : "border-white/10 bg-black/30 hover:border-white/25"
                    }`}
                  >
                    <span className="text-3xl">{a.emoji}</span>
                    <span className="text-xs font-sans font-medium">{a.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-fuchsia-400/30 bg-fuchsia-900/10 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Wand2 className="w-4 h-4 text-fuchsia-300" />
                <span className="text-xs uppercase tracking-widest font-bold text-fuchsia-200">{t("profile.portrait_title")}</span>
              </div>
              <p className="text-xs text-stone-400 mb-3">{t("profile.portrait_desc")}</p>
              <Input
                data-testid="input-portrait-desc"
                value={portraitDesc}
                maxLength={280}
                onChange={(e) => setPortraitDesc(e.target.value)}
                placeholder={t("profile.portrait_placeholder")}
                className="bg-black/40 border-white/15 focus-visible:ring-fuchsia-400"
              />
              {portraitImg && (
                <img
                  src={`data:image/png;base64,${portraitImg}`}
                  alt={t("profile.portrait_alt")}
                  className="mt-3 w-32 h-32 rounded-2xl object-cover border-2 border-fuchsia-400/60 mx-auto shard-glow"
                  data-testid="portrait-preview"
                />
              )}
              {portraitLoading && (
                <div className="mt-3 flex items-center gap-2 text-sm text-fuchsia-200">
                  <Loader2 className="w-4 h-4 animate-spin" /> {t("profile.portrait_loading")}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest font-bold text-stone-300 mb-2">{t("profile.mode_label")}</label>
              <div className="grid sm:grid-cols-3 gap-3">
                <button
                  onClick={() => setMode("eveil")}
                  data-testid="mode-eveil"
                  className={`text-left rounded-2xl border-2 p-4 transition-all ${
                    mode === "eveil" ? "border-fuchsia-400 bg-fuchsia-500/10 glow-renaissance" : "border-white/10 bg-black/30 hover:border-white/25"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">🧸</span>
                    <span className="font-serif text-lg font-bold text-stone-50">{t("profile.mode_eveil")}</span>
                    <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500/20 text-fuchsia-200 font-bold">{t("profile.mode_age_toddler")}</span>
                  </div>
                  <p className="text-sm text-stone-300 leading-relaxed">{t("profile.mode_eveil_desc")}</p>
                </button>
                <button
                  onClick={() => setMode("discovery")}
                  data-testid="mode-discovery"
                  className={`text-left rounded-2xl border-2 p-4 transition-all ${
                    mode === "discovery" ? "border-emerald-400 bg-emerald-500/10 glow-medieval" : "border-white/10 bg-black/30 hover:border-white/25"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-4 h-4 text-emerald-300" />
                    <span className="font-serif text-lg font-bold text-stone-50">{t("profile.mode_discovery")}</span>
                    <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 font-bold">{t("profile.mode_age_child")}</span>
                  </div>
                  <p className="text-sm text-stone-300 leading-relaxed">{t("profile.mode_discovery_desc")}</p>
                </button>
                <button
                  onClick={() => setMode("engineer")}
                  data-testid="mode-engineer"
                  className={`text-left rounded-2xl border-2 p-4 transition-all ${
                    mode === "engineer" ? "border-teal-400 bg-teal-500/10 glow-future" : "border-white/10 bg-black/30 hover:border-white/25"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Cog className="w-4 h-4 text-teal-300" />
                    <span className="font-serif text-lg font-bold text-stone-50">{t("profile.mode_engineer")}</span>
                    <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-teal-500/20 text-teal-200 font-bold">{t("profile.mode_age_teen")}</span>
                  </div>
                  <p className="text-sm text-stone-300 leading-relaxed">{t("profile.mode_engineer_desc")}</p>
                </button>
              </div>
            </div>

            <Button
              onClick={handleStart}
              disabled={loading}
              size="lg"
              className="w-full bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold text-base py-6 rounded-full"
              data-testid="btn-create-profile"
            >
              <Compass className="w-5 h-5 mr-2" />
              {loading ? t("profile.submitting") : t("profile.submit")}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
